import { NAVY_ACTION, fmtDate, fmtPhone } from "./shared";
import { hubspotReadinessChecks } from "./hubspotReadiness";
import { SectionBlock, ReadRow, EditRow, SelectRow } from "./SectionWidgets";
import { selectChoices } from "../../config/turnoverOptions";
import { hasMarketingKickoff } from "../../config/turnoverLayout";
import { PUBLIC_REFERENCE } from "../../config/marketingKickoff";
import { Choices } from "./MarketingKickoffPanel";
import { isNoCommission } from "./commission";

const HUBSPOT_PORTAL_ID = "44218274";

export default function CoverSheetPanel({
  turnover,
  optionRows = [],
  sections,
  activeSection,
  coverForm,
  setCover,
  coverDirty,
  savingCover,
  hsCompanies,
  hsContacts,
  hsLineItems,
  hsLoading,
  hsCacheTime,
  pmList,
  pmListLoading,
  directoryFailed,
  isAssignedOwner,
  getCoverSheetMissing,
  onToggleComplete,
  onRefreshHubSpot,
  onSave,
  onCancel,
  addCommissionRow,
  setNoCommission = () => {},
  updateCommissionRow,
  removeCommissionRow,
  goToSection,
  readOnly = false,
}) {
  const noCommission = isNoCommission(coverForm.commission_splits);
  return (
    <div>
      {/* Cover Sheet header with HubSpot link + Refresh */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: "var(--text)" }}>Cover Sheet</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {turnover.HubSpotDealID && (
            <a
              href={`https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/deal/${turnover.HubSpotDealID}`}
              target="_blank" rel="noreferrer"
              style={{
                fontSize: 12, fontWeight: 600, color: "var(--cs-blue)",
                textDecoration: "none", display: "flex", alignItems: "center", gap: 4,
                padding: "5px 12px", border: "1px solid var(--cs-border)",
                borderRadius: 6, background: "var(--surface)",
              }}
            >
              <span>🔗</span> HubSpot Deal #{turnover.HubSpotDealID}
            </a>
          )}
          {turnover.HubSpotDealID && (
            <button
              onClick={onRefreshHubSpot}
              disabled={hsLoading}
              style={{
                fontSize: 12, fontWeight: 600, color: "var(--text2)",
                padding: "5px 12px", border: "1px solid var(--cs-border)",
                borderRadius: 6, background: "var(--surface)", cursor: "pointer",
                opacity: hsLoading ? 0.5 : 1,
              }}
            >
              {hsLoading ? "Refreshing…" : hsCacheTime ? `↻ Refreshed ${new Date(hsCacheTime).toLocaleDateString("en-US", {month:"short", day:"numeric"})}` : "↻ Refresh HubSpot"}
            </button>
          )}
          {!readOnly && (
            <button
              onClick={onToggleComplete}
              disabled={!isAssignedOwner(activeSection)}
              title={
                !isAssignedOwner(activeSection)
                  ? `Only ${activeSection.AssignedTo || "the assigned owner"} can mark this complete`
                  : activeSection.IsComplete ? "Click to reopen" : "Mark the Cover Sheet complete"
              }
              style={{
                padding: "5px 12px", fontSize: 12, fontWeight: 600,
                border: "1px solid " + (activeSection.IsComplete ? "#10b981" : "var(--cs-border)"),
                borderRadius: 6,
                cursor: !isAssignedOwner(activeSection) ? "not-allowed" : "pointer",
                background: activeSection.IsComplete ? "#d1fae5" : "transparent",
                color: activeSection.IsComplete ? "#065f46" : "var(--text3)",
                opacity: !isAssignedOwner(activeSection) ? 0.5 : 1,
              }}
            >
              {activeSection.IsComplete ? "✓ Complete" : "Mark Complete"}
            </button>
          )}
        </div>
      </div>

      {/* Incomplete hint */}
      {!readOnly && !activeSection.IsComplete && (() => {
        const missing = getCoverSheetMissing();
        if (missing.length === 0) return null;
        return (
          <div style={{
            marginBottom: 20, padding: "12px 16px",
            background: "#FFFBEB", border: "1px solid #F59E0B",
            borderRadius: 8,
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#92400E", marginBottom: 6 }}>
              Cannot Complete Because
            </div>
            {missing.map((m, i) => (
              <div key={i} style={{ fontSize: 12, color: "#92400E", display: "flex", alignItems: "center", gap: 6, marginTop: i > 0 ? 3 : 0 }}>
                <span>□</span> {m}
              </div>
            ))}
          </div>
        );
      })()}

      {/* Missing contacts warning */}
      {!readOnly && !hsLoading && turnover.HubSpotDealID && hsContacts.length >= 0 && (() => {
        const REQUIRED_ROLES = ["Contract Contact", "Billing Contact", "End User Contact"];
        const presentRoles = new Set(hsContacts.map(c => c.label));
        const missing = REQUIRED_ROLES.filter(r => !presentRoles.has(r));
        if (missing.length === 0) return null;
        return (
          <div style={{
            marginBottom: 20, padding: "12px 16px",
            background: "#fff7ed", border: "1px solid #fb923c",
            borderRadius: 8, display: "flex", alignItems: "flex-start", gap: 10,
          }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>⚠️</span>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>
                Missing required contacts in HubSpot
              </div>
              <div style={{ fontSize: 12, color: "#b45309" }}>
                The following contact roles must be added to this deal in HubSpot before turnover can proceed:{" "}
                <strong>{missing.join(", ")}</strong>
              </div>
              <a
                href={`https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/deal/${turnover.HubSpotDealID}`}
                target="_blank" rel="noreferrer"
                style={{ fontSize: 12, color: "var(--cs-blue)", fontWeight: 600, marginTop: 4, display: "inline-block" }}
              >
                Open deal in HubSpot to add contacts →
              </a>
            </div>
          </div>
        );
      })()}

      {/* Project Identity — read-only from HubSpot */}
      <SectionBlock title="Project Identity" icon="🏗">
        <ReadRow label="Project Name"       value={turnover.ProjectName} />
        <ReadRow label="Job #"              value={turnover.JobNumber || "Pending"} />
        <ReadRow label="HubSpot Deal #"     value={turnover.HubSpotDealID} />
        <ReadRow label="Planned Turnover Date" value={fmtDate(coverForm.planned_turnover_date || turnover.PlannedTurnoverDate)} />
        <ReadRow label="Market"             value={turnover.Market} />
        <ReadRow label="Project Type"       value={turnover.ProjectType} />
        <ReadRow label="Square Feet"        value={turnover.SquareFeet ? `${parseInt(turnover.SquareFeet).toLocaleString()} SF` : null} />
        <ReadRow label="Deal Amount" value={(() => {
          if (hsLineItems.length > 0) {
            const total = hsLineItems.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
            return "$" + total.toLocaleString("en-US", { maximumFractionDigits: 0 });
          }
          return turnover.DealAmount ? "$" + parseFloat(turnover.DealAmount).toLocaleString("en-US", { maximumFractionDigits: 0 }) : null;
        })()} />
        <ReadRow label="Lead Source"        value={turnover.LeadSource} />
        <ReadRow label="Lead Source Detail" value={turnover.LeadSourceDetail} />
        <ReadRow label="Deal Description"   value={turnover.DealDescription} />
      </SectionBlock>

      {/* CleanSpace Team — read-only */}
      <SectionBlock title="CleanSpace Team" icon="👥">
        <ReadRow label="Sales Lead"         value={turnover.Salesperson} />
        <ReadRow label="Sales Lead Email"   value={turnover.SalespersonEmail} />
        <ReadRow label="BD2 / Deal Owner 2" value={turnover.SalesRep2} />
        <ReadRow label="Estimator"          value={turnover.Estimator} />
        <ReadRow label="Contract Structure" value={turnover.ContractStructure} />
        <ReadRow label="Labor Strategy"     value={turnover.LaborStrategy} />
        <ReadRow label="Arch Materials"     value={turnover.ArchMaterials} />
      </SectionBlock>

      {/* Project Assignment — editable, turnover only */}
      {!readOnly && (
        <SectionBlock title="Project Assignment" icon="📌">
          <SelectRow
            label="Project Manager"
            value={coverForm.project_manager}
            onChange={v => setCover("project_manager", v)}
            options={pmList}
            loading={pmListLoading}
            fetchFailed={directoryFailed}
            required
          />
          <EditRow label="Planned Turnover Date" value={coverForm.planned_turnover_date}
            onChange={v => setCover("planned_turnover_date", v)} type="date" required />
        </SectionBlock>
      )}

      {/* HubSpot Readiness */}
      {!readOnly && !hsLoading && turnover.HubSpotDealID && (() => {
        const coLabels  = hsCompanies.map(c => (c.label || "").toLowerCase());
        const ctLabels  = hsContacts.map(c => (c.label || "").toLowerCase());

        // Owner and Contract Holder are two separate required labels
        // (hubspotReadiness.js) — the same company may carry both.
        const checks = hubspotReadinessChecks({
          companyLabels: coLabels, contactLabels: ctLabels,
          companyCount: new Set(hsCompanies.map(c => c.id)).size, lineItemCount: hsLineItems.length,
        }).map(c => ({ ...c, hard: true }));

        const passed   = checks.filter(c => c.pass).length;
        const total    = checks.length;
        const allPass  = passed === total;
        const hardFail = checks.filter(c => c.hard && !c.pass);
        const pct      = Math.round((passed / total) * 100);

        return (
          <div style={{ marginBottom: 20, border: "1px solid", borderColor: allPass ? "#10b981" : hardFail.length ? "#ef4444" : "#f59e0b", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: allPass ? "#d1fae5" : hardFail.length ? "#fee2e2" : "#fef3c7" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: allPass ? "#065f46" : hardFail.length ? "#991b1b" : "#92400e" }}>🎯 HubSpot Readiness</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: allPass ? "#065f46" : hardFail.length ? "#991b1b" : "#92400e" }}>{pct}% &nbsp;({passed}/{total})</div>
            </div>
            <div style={{ height: 4, background: "#e5e7eb" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: allPass ? "#10b981" : hardFail.length ? "#ef4444" : "#f59e0b", transition: "width 0.4s" }} />
            </div>
            <div style={{ padding: "10px 16px", display: "flex", flexDirection: "column", gap: 6 }}>
              {checks.map(c => (
                <div key={c.key} style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 12 }}>
                  <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>{c.pass ? "✅" : c.hard ? "🔴" : "⚠️"}</span>
                  <div>
                    <span style={{ fontWeight: 600, color: c.pass ? "#065f46" : c.hard ? "#991b1b" : "#92400e" }}>{c.label}</span>
                    {!c.pass && <div style={{ fontSize: 11, color: "#6b7280", marginTop: 1 }}>{c.failMsg}</div>}
                  </div>
                </div>
              ))}
            </div>
            {hardFail.length > 0 && (
              <div style={{ padding: "8px 16px", borderTop: "1px solid #fca5a5", background: "#fff5f5", fontSize: 11, color: "#991b1b" }}>
                {hardFail.length} blocking issue{hardFail.length > 1 ? "s" : ""} — refresh HubSpot after fixing, then re-check
              </div>
            )}
          </div>
        );
      })()}

      {/* Release Schedule — from HubSpot Line Items */}
      <SectionBlock title="Opportunity Release Schedule" icon="📊">
        {hsLoading && <div style={{ fontSize: 12, color: "var(--text3)" }}>Loading from HubSpot…</div>}
        {!hsLoading && hsLineItems.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--text3)", fontStyle: "italic" }}>
            No line items found — add line items to this deal in HubSpot
          </div>
        )}
        {!hsLoading && hsLineItems.length > 0 && (() => {
          const totalAmt = hsLineItems.reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
          const releasedAmt = hsLineItems
            .filter(li => li.awarded === "true" || li.awarded === true)
            .reduce((s, li) => s + (parseFloat(li.amount) || 0), 0);
          const pct = totalAmt > 0 ? Math.round(releasedAmt / totalAmt * 100) : 0;
          return (
            <div>
              <div style={{ display: "flex", gap: 24, marginBottom: 14, padding: "10px 14px", background: "#f8f9fb", borderRadius: 8, border: "1px solid var(--cs-border)" }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Opportunity</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text)" }}>${totalAmt.toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Released</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#059669" }}>${releasedAmt.toLocaleString("en-US", { maximumFractionDigits: 0 })} <span style={{ fontSize: 11, fontWeight: 400 }}>({pct}%)</span></div>
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Forecasted</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#f59e0b" }}>${(totalAmt - releasedAmt).toLocaleString("en-US", { maximumFractionDigits: 0 })}</div>
                </div>
                <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <div style={{ width: "100%", height: 8, background: "#e5e7eb", borderRadius: 99, overflow: "hidden" }}>
                    <div style={{ width: `${pct}%`, height: "100%", background: "#059669", borderRadius: 99, transition: "width 0.3s" }} />
                  </div>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 70px 90px 100px 120px", gap: 0 }}>
                {["Line Item", "Amount", "SF", "$/SF", "Status", "Release Date"].map(h => (
                  <div key={h} style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", padding: "6px 10px", borderBottom: "2px solid var(--cs-border)" }}>{h}</div>
                ))}
                {hsLineItems
                  .sort((a, b) => {
                    const aR = a.awarded === "true" || a.awarded === true;
                    const bR = b.awarded === "true" || b.awarded === true;
                    if (aR !== bR) return aR ? -1 : 1;
                    return (parseFloat(b.amount) || 0) - (parseFloat(a.amount) || 0);
                  })
                  .map((li, i) => {
                    const released    = li.awarded === "true" || li.awarded === true;
                    const amt         = parseFloat(li.amount) || 0;
                    const sf          = li.squarefootage ? parseFloat(li.squarefootage) : null;
                    const dollarPerSf = li.dollar_per_sf ? parseFloat(li.dollar_per_sf) : null;
                    const releaseDate = li.release_date
                      ? new Date(li.release_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                      : li.end_date
                        ? `Est. ${new Date(li.end_date).toLocaleDateString("en-US", { month: "short", year: "numeric" })}`
                        : "—";
                    const rowBg = i % 2 === 0 ? "transparent" : "#fafbfc";
                    return [
                      <div key={`n${i}`} style={{ padding: "8px 10px", background: rowBg, fontSize: 13, fontWeight: 500, borderBottom: "1px solid var(--cs-border)" }}>
                        {li.name || "Unnamed"}
                        {li.description && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>{li.description}</div>}
                      </div>,
                      <div key={`a${i}`} style={{ padding: "8px 10px", background: rowBg, fontSize: 13, fontWeight: 600, borderBottom: "1px solid var(--cs-border)" }}>
                        ${amt.toLocaleString("en-US", { maximumFractionDigits: 0 })}
                      </div>,
                      <div key={`sf${i}`} style={{ padding: "8px 10px", background: rowBg, fontSize: 12, color: "var(--text2)", borderBottom: "1px solid var(--cs-border)" }}>
                        {sf !== null ? sf.toLocaleString("en-US", { maximumFractionDigits: 0 }) : "—"}
                      </div>,
                      <div key={`dsf${i}`} style={{ padding: "8px 10px", background: rowBg, fontSize: 12, color: "var(--text2)", borderBottom: "1px solid var(--cs-border)" }}>
                        {dollarPerSf !== null ? `$${dollarPerSf.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}
                      </div>,
                      <div key={`s${i}`} style={{ padding: "8px 10px", background: rowBg, borderBottom: "1px solid var(--cs-border)" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 100, background: released ? "#d1fae5" : "#fef3c7", color: released ? "#065f46" : "#92400e" }}>
                          {released ? "✓ Released" : "Forecasted"}
                        </span>
                      </div>,
                      <div key={`d${i}`} style={{ padding: "8px 10px", background: rowBg, fontSize: 12, color: "var(--text2)", borderBottom: "1px solid var(--cs-border)" }}>
                        {releaseDate}
                      </div>,
                    ];
                  })}
              </div>
            </div>
          );
        })()}
      </SectionBlock>

      {/* Jobsite Address */}
      {!hsLoading && (() => {
        const jobsite = hsCompanies.find(co => {
          const l = (co.label || "").toLowerCase();
          return l.includes("jobsite") || l.includes("job site");
        });
        if (!jobsite) return null;
        return (
          <SectionBlock title="Jobsite Address" icon="📍">
            <div style={{ padding: "12px 14px", background: "#eff6ff", border: "1px solid #93c5fd", borderRadius: 8 }}>
              {jobsite.name && <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", marginBottom: 3 }}>{jobsite.name}</div>}
              {jobsite.address && <div style={{ fontSize: 13, color: "var(--text2)" }}>{jobsite.address}</div>}
              {(jobsite.city || jobsite.state) && <div style={{ fontSize: 13, color: "var(--text2)" }}>{[jobsite.city, jobsite.state, jobsite.zip].filter(Boolean).join(", ")}</div>}
              {!jobsite.address && !jobsite.city && (
                <div style={{ fontSize: 12, color: "var(--text3)", fontStyle: "italic" }}>Tagged in HubSpot but no address fields set on the company record</div>
              )}
            </div>
          </SectionBlock>
        );
      })()}

      {/* Companies */}
      <SectionBlock title="Companies" icon="🏢">
        {hsLoading && <div style={{ fontSize: 12, color: "var(--text3)", padding: "8px 0" }}>Loading from HubSpot…</div>}
        {!hsLoading && !turnover.HubSpotDealID && (
          <div style={{ fontSize: 12, color: "var(--text3)", fontStyle: "italic" }}>No HubSpot deal linked</div>
        )}
        {!hsLoading && hsCompanies.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {hsCompanies.filter(co => {
              const l = (co.label || "").toLowerCase();
              return !l.includes("jobsite") && !l.includes("job site");
            }).map((co, i) => (
              <div key={i} style={{ padding: "12px 14px", background: "#f8f9fb", border: "1px solid var(--cs-border)", borderRadius: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--cs-blue)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>{co.label}</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)", marginBottom: 3 }}>{co.name || "—"}</div>
                {co.address && <div style={{ fontSize: 11, color: "var(--text2)" }}>{co.address}</div>}
                {(co.city || co.state) && <div style={{ fontSize: 11, color: "var(--text2)" }}>{[co.city, co.state, co.zip].filter(Boolean).join(", ")}</div>}
                {co.phone && <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 2 }}>📞 {fmtPhone(co.phone)}</div>}
              </div>
            ))}
          </div>
        )}
        {!hsLoading && hsCompanies.length === 0 && turnover.HubSpotDealID && (
          <div style={{ fontSize: 12, color: "var(--text3)", fontStyle: "italic" }}>No companies associated with this deal in HubSpot</div>
        )}
      </SectionBlock>

      {/* Contacts */}
      <SectionBlock title="Contacts" icon="📇">
        {hsLoading && <div style={{ fontSize: 12, color: "var(--text3)" }}>Loading from HubSpot…</div>}
        {!hsLoading && hsContacts.length === 0 && (
          <div style={{ fontSize: 12, color: "var(--text3)", fontStyle: "italic" }}>
            No contacts associated — add contacts with roles in HubSpot and click Refresh
          </div>
        )}
        {!hsLoading && hsContacts.length > 0 && (
          <div>
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr 1fr 1fr 1fr", gap: 8, padding: "6px 0", borderBottom: "1px solid var(--cs-border)", marginBottom: 6 }}>
              {["Role", "Name", "Title", "Email", "Phone"].map(h => (
                <div key={h} style={{ fontSize: 10, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{h}</div>
              ))}
            </div>
            {hsContacts.map((c, i) => (
              <div key={i} style={{
                display: "grid", gridTemplateColumns: "140px 1fr 1fr 1fr 1fr",
                gap: 8, padding: "8px 0",
                borderBottom: i < hsContacts.length - 1 ? "1px solid var(--cs-border)" : "none",
                alignItems: "center",
              }}>
                <div style={{
                  fontSize: 10, fontWeight: 700, color: "var(--cs-blue)",
                  background: "#eff6ff", padding: "2px 7px", borderRadius: 100,
                  textTransform: "uppercase", letterSpacing: "0.04em",
                  display: "inline-block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {c.label}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600 }}>{c.name || "—"}</div>
                <div style={{ fontSize: 12, color: "var(--text2)" }}>{c.title || "—"}</div>
                <div style={{ fontSize: 12, color: "var(--text2)" }}>{c.email || "—"}</div>
                <div style={{ fontSize: 12, color: "var(--text2)" }}>{fmtPhone(c.phone) || "—"}</div>
              </div>
            ))}
          </div>
        )}
      </SectionBlock>

      {/* Client confidentiality — layout 3+, required to complete §1 (Sales
          knows it earliest, often from the NDA). Shown on §18 and the package. */}
      {!readOnly && hasMarketingKickoff(turnover) && (
        <SectionBlock title="Client confidentiality" icon="🔒">
          <div style={{ fontSize: 11, fontWeight: 600, color: coverForm.public_reference ? "var(--text3)" : "#92400E", marginBottom: 6 }}>
            Can this project be publicly referenced?<span style={{ color: "#F59E0B", marginLeft: 3 }}>●</span>
          </div>
          <div style={{ marginBottom: 12 }}>
            <Choices options={PUBLIC_REFERENCE} value={coverForm.public_reference || ""} onChange={v => setCover("public_reference", v)} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <EditRow label="Confidentiality notes (NDA terms, naming guidelines, …)" value={coverForm.confidentiality_notes || ""}
              onChange={v => setCover("confidentiality_notes", v)} multiline />
            <EditRow label="Photography restrictions / other limits" value={coverForm.photography_restrictions || ""}
              onChange={v => setCover("photography_restrictions", v)} multiline />
          </div>
        </SectionBlock>
      )}

      {/* Commission — turnover only */}
      {!readOnly && (
        <SectionBlock title="Commission" icon="🤝">
        <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 10 }}>
          Log who contributed to this deal and their split. This is a record only — payout amounts and schedules live in the Commission Ledger.
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 600, color: "var(--text)", cursor: "pointer", marginBottom: 12 }}>
          <input type="checkbox" checked={noCommission} onChange={e => setNoCommission(e.target.checked)} />
          No commission is paid on this job
        </label>
        {noCommission ? (
          <div style={{ fontSize: 12, color: "var(--text2)", padding: "8px 12px", background: "var(--surface2)", border: "1px solid var(--cs-border)", borderRadius: 6 }}>
            Recorded as not applicable — no contributors or splits are needed. Click Save to keep it.
          </div>
        ) : (<>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 0.8fr 32px", gap: 8, alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)" }}>Name</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)" }}>Role</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)" }}>Split %</div>
          <div />
        </div>
        {(coverForm.commission_splits || []).map(row => (
          <div key={row.id} style={{ display: "grid", gridTemplateColumns: "2fr 1.2fr 0.8fr 32px", gap: 8, alignItems: "center", marginBottom: 8 }}>
            <input
              value={row.name}
              onChange={e => updateCommissionRow(row.id, "name", e.target.value)}
              placeholder="Name"
              style={{ padding: "6px 10px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }}
            />
            <select
              value={row.role}
              onChange={e => updateCommissionRow(row.id, "role", e.target.value)}
              style={{ padding: "6px 10px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }}
            >
              {selectChoices("commission_roles", optionRows, row.role).map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <input
              type="number" min="0" max="100" step="0.1"
              value={row.split_pct}
              onChange={e => updateCommissionRow(row.id, "split_pct", e.target.value)}
              placeholder="%"
              style={{ padding: "6px 10px", fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", color: "var(--text)" }}
            />
            <button
              onClick={() => removeCommissionRow(row.id)}
              title="Remove"
              style={{ width: 28, height: 28, border: "1px solid var(--cs-border)", borderRadius: 6, background: "none", color: "var(--text2)", cursor: "pointer" }}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          onClick={addCommissionRow}
          style={{ marginTop: 4, padding: "6px 14px", fontSize: 12, fontWeight: 600, background: "none", border: "1px dashed var(--cs-border)", borderRadius: 6, cursor: "pointer", color: "var(--text2)" }}
        >
          + Add contributor
        </button>
        {(coverForm.commission_splits || []).length > 0 && (() => {
          const total   = (coverForm.commission_splits || []).reduce((sum, r) => sum + (parseFloat(r.split_pct) || 0), 0);
          const rounded = Math.round(total * 10) / 10;
          const ok      = Math.abs(rounded - 100) < 0.05;
          return (
            <div style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: ok ? "var(--text2)" : "#b45309" }}>
              Total: {rounded}% {!ok && "— splits should add up to 100%"}
            </div>
          );
        })()}
        </>)}
        </SectionBlock>
      )}

      {/* Navigation buttons — turnover only */}
      {!readOnly && (() => {
        const nextSec   = sections.find(s => s.SectionNumber === 2);
        const nextLabel = nextSec ? nextSec.SectionName : "Contract Review & Sign-Off";
        return (
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 28, paddingTop: 20, borderTop: "1px solid var(--cs-border)" }}>
            <button
              onClick={onCancel}
              style={{ padding: "8px 18px", fontSize: 13, fontWeight: 600, background: "none", border: "1px solid var(--cs-border)", borderRadius: 6, cursor: "pointer", color: "var(--text2)" }}
            >
              Cancel
            </button>
            <button
              onClick={() => goToSection(2)}
              style={{ padding: "8px 18px", fontSize: 13, fontWeight: 600, background: "none", border: "1.5px solid var(--cs-border)", borderRadius: 6, cursor: "pointer", color: "var(--text)" }}
            >
              Next →
            </button>
            <button
              onClick={async () => { await onSave(); goToSection(2); }}
              disabled={savingCover || !coverDirty}
              style={{
                padding: "9px 20px", fontSize: 13, fontWeight: 700,
                background: NAVY_ACTION, color: "#fff",
                border: "none", borderRadius: 6, cursor: "pointer",
                opacity: (savingCover || !coverDirty) ? 0.5 : 1,
                boxShadow: (savingCover || !coverDirty) ? "none" : "0 1px 3px rgba(30,58,95,0.35)",
              }}
            >
              {savingCover ? "Saving…" : `Save + Next: ${nextLabel} →`}
            </button>
          </div>
        );
      })()}
    </div>
  );
}
